import React, { useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 499 (Kamel) — 🔗 liens cliquables + 📎 pièces jointes (PDF, image, Word…) partagés par les notes et les formalités.
export const LiensEditor = ({ liens = [], onChange, getAuthConfig, testPrefix = 'lien', titre, aide, legendeDefaut = '' }) => {
  // Iter 655 — légende proposée automatiquement à partir du titre de la note (sans emoji), numérotée s'il y a déjà des photos
  const legendeAuto = () => {
    const base = (legendeDefaut || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
    if (!base) return '';
    const nb = liens.filter(l => l.image).length;
    return nb > 0 ? `${base} (${nb + 1})` : base;
  };
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const camRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const setAt = (i, patch) => onChange(liens.map((x, j) => j === i ? { ...x, ...patch } : x));
  // Iter 654 — photos de téléphone (5-12 Mo) réduites côté navigateur (1600 px, JPEG) : upload rapide, notes légères hors ligne
  const compresser = (file) => new Promise((resolve) => {
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type) || file.size < 400 * 1024) return resolve(file);
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      const k = Math.min(1, 1600 / Math.max(im.width, im.height));
      const c = document.createElement('canvas');
      c.width = Math.round(im.width * k); c.height = Math.round(im.height * k);
      c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
      c.toBlob(b => { URL.revokeObjectURL(url); resolve(b ? new File([b], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file); }, 'image/jpeg', 0.85);
    };
    im.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    im.src = url;
  });
  const upload = async (file, forceImage = false) => {
    if (!file) return;
    setUploading(true);
    try {
      if (forceImage) file = await compresser(file);
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/pieces-jointes/upload`, fd, { ...getAuthConfig(), headers: { ...(getAuthConfig().headers || {}), 'Content-Type': 'multipart/form-data' } });
      const estImage = forceImage || /\.(jpe?g|png|webp)$/i.test(file.name);
      onChange([...liens, { label: estImage ? legendeAuto() : file.name.replace(/\.[^.]+$/, ''), url: r.data.url, fichier: true, image: estImage }]);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setUploading(false);
    for (const ref of [fileRef, imgRef, camRef]) if (ref.current) ref.current.value = '';
  };
  return (
    <div className="text-xs font-bold text-gray-700" data-testid={`${testPrefix}-editor`}>
      {titre || 'Liens utiles, documents & photos'} <span className="font-normal text-gray-500">{aide || "(site officiel ESTA, formulaire PDF, plan ou affiche en photo…) — cliquables dans l'espace adhérent ET dans les e-mails ; les photos s'affichent directement dans la note"}</span>
      {liens.map((l, i) => (
        <div key={i} className="flex gap-1.5 mt-1 items-center" data-testid={`${testPrefix}-row-${i}`}>
          {l.image && l.url
            ? <img src={l.url} alt="" className="h-10 w-14 object-cover rounded border border-gray-200 flex-shrink-0" data-testid={`${testPrefix}-thumb-${i}`} />
            : <span title={l.fichier ? 'Pièce jointe' : 'Lien'}>{l.fichier ? '📄' : '🔗'}</span>}
          <input value={l.label || ''} onChange={e => setAt(i, { label: e.target.value })}
            placeholder={l.image ? 'Légende (ex : Plan du rendez-vous)' : 'Libellé (ex : Site officiel ESTA)'} className="border rounded-lg px-2 py-1 text-xs flex-1 font-normal" data-testid={`${testPrefix}-label-${i}`} />
          {l.fichier
            ? <span className="flex-[2] flex items-center gap-2 font-normal">
                <a href={l.url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-700 underline truncate" data-testid={`${testPrefix}-open-${i}`}>{l.image ? 'voir la photo ↗' : 'ouvrir le document ↗'}</a>
                {/\.(jpe?g|png|webp)(\?|$)/i.test(l.url || '') || l.image
                  ? <label className="text-[10px] text-gray-600 cursor-pointer whitespace-nowrap" title="Cochée : la photo s'affiche en grand directement dans la note. Décochée : simple lien « ouvrir »."><input type="checkbox" checked={!!l.image} onChange={e => setAt(i, { image: e.target.checked })} data-testid={`${testPrefix}-image-toggle-${i}`} /> 🖼️ afficher dans la note</label>
                  : null}
              </span>
            : <input value={l.url || ''} onChange={e => setAt(i, { url: e.target.value })}
              placeholder="https://…" className="border rounded-lg px-2 py-1 text-xs flex-[2] font-normal" data-testid={`${testPrefix}-url-${i}`} />}
          <button type="button" onClick={() => onChange(liens.filter((_, j) => j !== i))} className="text-red-500 font-bold text-xs" data-testid={`${testPrefix}-remove-${i}`}>🗑</button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 mt-1.5">
        <button type="button" onClick={() => onChange([...liens, { label: '', url: '', fichier: false }])}
          className="text-[11px] px-2 py-1 bg-gray-100 border rounded-lg font-bold" data-testid={`${testPrefix}-add`}>➕ Ajouter un lien</button>
        <button type="button" onClick={() => imgRef.current?.click()} disabled={uploading}
          className="text-[11px] px-2 py-1 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg font-bold disabled:opacity-50" data-testid={`${testPrefix}-photo-btn`}>
          {uploading ? '⏳ Envoi…' : '🖼️ Ajouter une photo (plan, affiche — visible directement dans la note)'}
        </button>
        <button type="button" onClick={() => camRef.current?.click()} disabled={uploading}
          className="text-[11px] px-2 py-1 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg font-bold disabled:opacity-50" title="Sur téléphone : ouvre directement l'appareil photo" data-testid={`${testPrefix}-camera-btn`}>
          {uploading ? '⏳ Envoi…' : '📷 Prendre une photo'}
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="text-[11px] px-2 py-1 bg-sky-50 border border-sky-300 text-sky-900 rounded-lg font-bold disabled:opacity-50" data-testid={`${testPrefix}-upload-btn`}>
          {uploading ? '⏳ Envoi…' : '📎 Joindre un document (PDF, Word — 15 Mo max)'}
        </button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={e => upload(e.target.files?.[0], true)} data-testid={`${testPrefix}-camera-input`} />
        <input ref={imgRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden"
          onChange={e => upload(e.target.files?.[0], true)} data-testid={`${testPrefix}-photo-input`} />
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt" className="hidden"
          onChange={e => upload(e.target.files?.[0])} data-testid={`${testPrefix}-upload-input`} />
      </div>
    </div>
  );
};

export default LiensEditor;
